// views/Login/PostLogin.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import PropTypes from "prop-types";
import axios from "axios";
import {
  Box,
  Typography,
} from "@mui/material";
import Loading from "../../components/Loading/Loading";
import { useNavigate } from "react-router-dom";
import PreferencesModal from "./components/PreferencesModal";

const SERVER_BASE_URL = process.env.REACT_APP_SERVER_BASE_URL;

const loadingSpinner = (
  <Box
    sx={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
    }}
  >
    <Loading />
  </Box>
);

const PostLogin = ({ setIsLoginSuccessful }) => {
  const { isAuthenticated, user, getAccessTokenSilently, logout } = useAuth0();
  const navigate = useNavigate();

  const [accessToken, setAccessToken] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [apiError, setApiError] = useState(null);

  const [showPrefsModal, setShowPrefsModal] = useState(false);
  const [colleges, setColleges] = useState([]);
  const [initialMode, setInitialMode] = useState("");
  const [initialSelectedCollegeIds, setInitialSelectedCollegeIds] = useState(
    []
  );
  const [ownCollegeName, setOwnCollegeName] = useState(null);

  const hasPasswordlessToken = !!localStorage.getItem("vibeu_access_token");

  useEffect(() => {
    const collegeNameFromStorage = localStorage.getItem("selectedSchoolName");
    if (collegeNameFromStorage) {
      setOwnCollegeName(collegeNameFromStorage);
    }
  }, []);

  // 1) Get access token (from Auth0 SDK OR from passwordless localStorage)
  useEffect(() => {
    const getToken = async () => {
      try {
        if (hasPasswordlessToken) {
          const token = localStorage.getItem("vibeu_access_token");
          if (!token) return;
          setAccessToken(token);
          return;
        }

        // normal Auth0 (Google / Microsoft) flow
        if (!isAuthenticated || !user) return;

        const token = await getAccessTokenSilently();
        setAccessToken(token);
      } catch (err) {
        console.error("Error getting access token:", err);
        setApiError("Failed to authenticate.");
      }
    };

    getToken();
  }, [hasPasswordlessToken, isAuthenticated, user, getAccessTokenSilently]);

  // 2) Call /post-login (for social logins) and then /user/basic
  useEffect(() => {
    const initPostLogin = async () => {
      if (!accessToken) return;

      try {
        const selectedSchoolId = localStorage.getItem("selectedSchoolId");
        if (!selectedSchoolId) {
          throw new Error("Please select a school before logging in.");
        }

        // For **social logins**, we still need to hit /post-login here
        // For **passwordless**, we already did it in PasswordlessEmailLogin –
        // but calling it again with the same collegeId is idempotent, so it's safe.
        if (!hasPasswordlessToken && user) {
          const loginInfo = {
            ...user, // contains name, email, email_verified, etc.
            collegeId: selectedSchoolId,
          };

          await axios.post(`${SERVER_BASE_URL}/post-login`, loginInfo, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });
        }

        // --- Step 2: Fetch basic user info including matchingPreferences ---
        const userRes = await axios.get(`${SERVER_BASE_URL}/user/basic`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        const basicUser = userRes.data || {};
        const prefs = basicUser.matchingPreferences || {};
        const currentMode = prefs.mode || "";
        const currentCollegeIds = (prefs.collegeIds || []).map((id) =>
          typeof id === "string" ? id : id.toString()
        );

        // if preferences already set -> go straight to dashboard
        if (currentMode !== "") {
          setIsLoginSuccessful(true);
          navigate("/dashboard", { replace: true });
          return;
        }

        // otherwise show preferences modal
        const collegesRes = await axios.get(`${SERVER_BASE_URL}/colleges`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        const data = collegesRes.data;
        const collegesArray = Array.isArray(data) ? data : data.colleges || [];

        setColleges(collegesArray);
        setInitialMode(currentMode);
        setInitialSelectedCollegeIds(currentCollegeIds);
        setShowPrefsModal(true);
      } catch (err) {
        console.error("PostLogin error:", err);

        if (err.response?.data?.message) {
          alert(err.response.data.message);
        } else if (err.message) {
          alert(err.message);
        }

        setIsLoginSuccessful(false);

        if (hasPasswordlessToken) {
          // clear manual tokens and bounce to login
          localStorage.removeItem("vibeu_access_token");
          localStorage.removeItem("vibeu_id_token");
          window.location.href = "/login";
        } else {
          // normal Auth0 logout
          logout({
            logoutParams: {
              returnTo: window.location.origin,
            },
          });
        }
      } finally {
        setIsInitialLoading(false);
      }
    };

    initPostLogin();
  }, [
    accessToken,
    hasPasswordlessToken,
    isAuthenticated,
    user,
    navigate,
    logout,
    setIsLoginSuccessful,
  ]);

  // --- Render states ---
  // if neither Auth0 session nor passwordless token, we shouldn't be here
  if (!isAuthenticated && !hasPasswordlessToken) {
    return loadingSpinner;
  }

  if (isInitialLoading && !showPrefsModal) {
    return loadingSpinner;
  }

  return (
    <>
      {apiError && (
        <Box
          sx={{
            position: "absolute",
            top: "12px",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          <Typography variant="body2" color="error">
            {apiError}
          </Typography>
        </Box>
      )}

      <PreferencesModal
        open={showPrefsModal}
        colleges={colleges}
        initialMode={initialMode}
        initialSelectedCollegeIds={initialSelectedCollegeIds}
        onSave={async ({ mode, collegeIds }) => {
          try {
            await axios.put(
              `${SERVER_BASE_URL}/user/matching-preferences`,
              { mode, collegeIds },
              {
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                },
              }
            );
            setIsLoginSuccessful(true);
            navigate("/dashboard", { replace: true });
          } catch (err) {
            console.error("Error saving preferences:", err);
            if (err.response?.data?.message) {
              alert(err.response.data.message);
            } else {
              alert("Failed to save preferences. Please try again.");
            }
          }
        }}
        ownCollegeName={ownCollegeName}
      />
    </>
  );
};

PostLogin.propTypes = {
  setIsLoginSuccessful: PropTypes.func.isRequired,
};

export default PostLogin;
